import { useState, useEffect, JSX } from "react";
import PiwikPro from '@piwikpro/react-piwik-pro';
import useAzureAuth from "utils/hooks/useAzureAuth";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from '@azure/msal-browser';
import useSessionUser from "utils/hooks/useSessionUser";
import UpdateInfos from "pages/Update";
import { loginRequest } from "utils/auth";
import { redirectToService } from "utils/utils";
import apiClient from "utils/apiClient";
import ErrorPage from "./pages/Error";
import Loader from "./components/Loader/Loader";

PiwikPro.initialize(import.meta.env.REACT_APP_PIWIK_API_KEY || '', import.meta.env.REACT_APP_PIWIK_API_URL || "");

interface AppProps {
  isLoginPage?: boolean;
  isLogoutPage?: boolean;
}

function App({ isLoginPage = false, isLogoutPage = false }: AppProps) {
  const { instance, inProgress } = useMsal();
  const [view, setView] = useState<JSX.Element>(
    <div className="home-container">
      <Loader size={80} />
    </div>
  );

  // Hook permettant de vérifier l'authentification
  // Et de réouvrir la popup si besoin
  useAzureAuth();

  const isAuthenticated = useIsAuthenticated();
  // TODO: eventually remove this temp debug
  // const isAuthenticated = true;
  const { user } = useSessionUser();

  console.log("inProgress:", inProgress);
  console.log("user:", user);
  console.log("isAuthenticated:", isAuthenticated);
  console.log("isLoginPage:", isLoginPage);
  console.log("isLogoutPage:", isLogoutPage);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) {
      // MSAL non prêt, ignorez ou affichez loader via JSX
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const serviceId = searchParams.get("serviceId");
    const redirectURI = searchParams.get("redirectURI");

    const checkService = (serviceId: string | null, redirectURI: string | null): Promise<boolean> => {
      return new Promise((resolve) => {
        if (!serviceId || !redirectURI) {
          return errorView("Erreur : serviceId ou redirectURI manquants");
        }

        apiClient.post('/checkService', {
          serviceId: serviceId,
          redirectURI: redirectURI
        }).then(response => {
          if (response.status === 200) {
            // sauvegarder dans le local storage
            localStorage.setItem('serviceId', serviceId);
            localStorage.setItem('redirectURI', redirectURI);
            resolve(true);
          } else {
            errorView("Erreur les informations du service sont invalides");
            resolve(false);
          }
        }).catch(error => {
          if (error?.response?.data) {
            // récupérer le corps de la réponse d'erreur
            errorView(`Erreur: ${error?.response?.data?.error || 'Erreur inconnue'}`);
          } else {
            errorView(`Erreur lors de la vérification du service : ${error?.message}`);
          }
          resolve(false);
        });
      });
    }

    if (isAuthenticated && user) {
      if (inProgress === InteractionStatus.None && isLogoutPage) {
        checkService(serviceId, redirectURI).then((result) => {
          if (!result) return;
          // lancer la deconnexion
          void instance.logoutRedirect({
            postLogoutRedirectUri: redirectURI
          });
        });
        return;
      }

      if ((!user?.firstName && !user?.mdm?.firstName) ||
        (!user?.lastName && !user?.mdm?.lastName) ||
        !user?.email ||
        !user?.mdm?.gender ||
        !user?.mdm?.address?.postalCode ||
        !user?.mdm?.address?.housenumber ||
        !user?.mdm?.address?.street ||
        !user?.mdm?.address?.city ||
        !user?.mdm?.country ||
        !user?.mdm?.phoneNumber
      ) {
        return setView(<UpdateInfos />);
      } else if (!isLoginPage) {
        // rediriger vers le service si les infos sont complètes
        redirectToService();
      }
      return;
    } else if ((!isAuthenticated && isLoginPage) || (isAuthenticated && !user)) {
      return; // en cours de connexion
    }

    const errorView = (msg: string) => {
      console.error(msg);
      return setView(<ErrorPage message={msg} />);
    }

    if (inProgress === InteractionStatus.None && !isAuthenticated && !isLoginPage) {
      checkService(serviceId, redirectURI).then((result) => {
        if (!result) return;
        // lancer la connexion
        void instance.loginRedirect(loginRequest);
      });
    }

  }, [isAuthenticated, instance, user, isLoginPage, isLogoutPage, inProgress]);

  return (view);
}

export default App;
